import React, { useCallback, useState } from "react";
import {
    PlainModal,
    StandardModalBody,
    StandardModalFooter,
    StandardModalHeader,
} from "@design-system/Modal/Modal";
import { Button } from "@design-system/Button/Button";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { Loader } from "@design-system/Loader/Loader";
import { HStack } from "@design-system/HStack/HStack";
import { VStack } from "@design-system/VStack/VStack";
import fileDownload from "js-file-download";
import {
    FILE_TYPE_MIME_TYPES,
    FileType,
} from "@common/constants/financial-documents";
import {
    MapCoaTypesCsvResponse,
    MAX_COA_CSV_LENGTH,
} from "@common/dto/financialDocument/coa-type-mapping.schema";
import { FinancialDocument } from "@common/types/financialDocument";
import { AddDocument } from "@components/documents/FinancialDocumentList/AddDocument";
import { FinancialDocumentUploadProvider } from "@components/documents/FinancialDocumentUpload/FinancialDocumentUploadProvider";
import { useScope } from "@state/scopeContext.lib";
import { CSV_MIME, readBlobResponseAsText } from "@lib/fileDownload";
import { getFinancialDocumentFile } from "@lib/financialDocument";
import { useMapCoaTypesCsv } from "@api/fileScanning.api";
import { CheckIcon } from "@icons";
import styles from "./MercuryCoaTypeMappingModal.module.scss";

const CSV_ACCEPT = FILE_TYPE_MIME_TYPES[FileType.CSV];

interface MercuryCoaTypeMappingModalProps {
    show: boolean;
    onHide: () => void;
}

function downloadCsv(result: MapCoaTypesCsvResponse) {
    fileDownload(result.file, result.filename, CSV_MIME);
}

async function readFinancialDocumentCsv(documentId: number): Promise<string> {
    const response = await getFinancialDocumentFile(documentId);
    return await readBlobResponseAsText(response);
}

export const MercuryCoaTypeMappingModal: React.FC<
    MercuryCoaTypeMappingModalProps
> = ({ show, onHide }) => {
    const mapCoaTypesCsv = useMapCoaTypesCsv();
    const { error: toastError } = useToaster();
    const { workspaceId } = useScope();
    const [result, setResult] = useState<MapCoaTypesCsvResponse | null>(null);
    const [isReadingDocument, setIsReadingDocument] = useState(false);

    const isProcessing =
        !result && (isReadingDocument || mapCoaTypesCsv.isPending);
    const canUpload = !!workspaceId;

    const mapDocument = useCallback(
        async (documentId: number) => {
            if (!workspaceId) {
                return;
            }

            setResult(null);
            setIsReadingDocument(true);
            try {
                const csvContent = await readFinancialDocumentCsv(documentId);
                if (csvContent.length > MAX_COA_CSV_LENGTH) {
                    toastError("CSV is too large; the maximum is 2MB.");
                    return;
                }
                const mappedResponse = await mapCoaTypesCsv.mutateAsync({
                    workspaceId,
                    file: csvContent,
                });
                setResult(mappedResponse);
            } catch (error) {
                toastError(
                    error instanceof Error && error.message
                        ? error.message
                        : "Failed to map CoA types. Check the CSV format.",
                );
            } finally {
                setIsReadingDocument(false);
            }
        },
        [workspaceId, mapCoaTypesCsv, toastError],
    );

    const handleDocumentSelect = useCallback(
        async (documentId: number) => {
            if (isProcessing) {
                return;
            }
            await mapDocument(documentId);
        },
        [isProcessing, mapDocument],
    );

    const handleUploadCompleted = useCallback(
        async (uploadedDocuments: FinancialDocument[]) => {
            const financialDocument = uploadedDocuments[0];
            if (financialDocument) {
                await handleDocumentSelect(financialDocument.id);
            }
        },
        [handleDocumentSelect],
    );

    const handleHide = useCallback(() => {
        setResult(null);
        setIsReadingDocument(false);
        onHide();
    }, [onHide]);

    const renderStatus = () => {
        if (isProcessing) {
            return (
                <HStack
                    gap={8}
                    align="center"
                    paddingBlock={8}
                    className={styles.extractingState}
                >
                    <Loader inline />
                    <span>Processing...</span>
                </HStack>
            );
        }

        if (result) {
            return (
                <HStack
                    gap={8}
                    align="center"
                    paddingBlock={8}
                    className={styles.extractingState}
                >
                    <CheckIcon className="icon-color-gray-900" />
                    <span>Mapped!</span>
                </HStack>
            );
        }

        return null;
    };

    return (
        <PlainModal show={show} onHide={handleHide} size="md">
            <FinancialDocumentUploadProvider
                accept={CSV_ACCEPT}
                onCompleted={handleUploadCompleted}
            >
                <StandardModalHeader>
                    Map Mercury CoA to Kick Types
                </StandardModalHeader>
                <StandardModalBody className={styles.uploadStepModalBody}>
                    <VStack gap={8} className={styles.uploadStep}>
                        <label className={styles.uploadStepLabel}>
                            Chart of accounts
                        </label>
                        <p className={styles.uploadStepDescription}>
                            Upload a Mercury chart of accounts export. We'll map
                            each account to a Kick type and return an updated
                            CSV.
                        </p>
                        {!canUpload ? (
                            <p className={styles.uploadStepDescription}>
                                Select a workspace to use this tool.
                            </p>
                        ) : (
                            <>
                                <AddDocument
                                    attachDocument={handleDocumentSelect}
                                    attachedDocumentIds={[]}
                                    closeBrowseModalOnAttach
                                />
                                {renderStatus()}
                            </>
                        )}
                    </VStack>
                </StandardModalBody>
                <StandardModalFooter>
                    <Button
                        type="button"
                        variant="secondary"
                        size="xl"
                        onClick={handleHide}
                        disabled={isProcessing}
                    >
                        Close
                    </Button>
                    {result && (
                        <Button
                            type="button"
                            variant="primary"
                            size="xl"
                            onClick={() => downloadCsv(result)}
                        >
                            Download result
                        </Button>
                    )}
                </StandardModalFooter>
            </FinancialDocumentUploadProvider>
        </PlainModal>
    );
};
